import React, { useState, useEffect } from 'react'

import {
  InputMask,
  Row,
  DateRange,
  CurrencyInput,
  Grid,
  Input,
  SelectForm,
  AutoCompleteSearchTerm
} from 'components'
import { MagnifyingGlassIcon } from 'components/Icons'
import { useDebounce, useModal, useLoading } from 'hooks'
import { useFormContext, UseFormReturn } from 'react-hook-form'
import {
  ContractServices,
  ISearchContractEntityResponse
} from 'services/NGT/Contract'
import { DjurServices } from 'services/NGT/DjurServices'
import { SPACING, OLDYUP as YUP, FORMAT } from 'utils'
import * as Yup from 'yup'

import { useDjurService } from '../../../../context'

const requestDataFields = {
  cdContrato: YUP.required.object,
  chamadoCervelo: YUP.required.string,
  cpfReclamante: YUP.required.string,
  nmReclamante: YUP.requiredWhen('isAddNew').string,
  nuAnalises: YUP.string,
  nuProcesso: YUP.required.string,
  // valorContratual: YUP.required.number,
  tipo: YUP.required.string,
  // txObservacaoRetorno: YUP.requiredWhen('isReanalise').string
}

export const requestDataSchema = Yup.object()
  .shape(requestDataFields)
  .concat(YUP.dateRange('dtInicioReclamacao', 'dtFimReclamacao').notRequired)

interface IRequestDataComponent {
  isConsult: boolean
}

const RequestData: React.FC<IRequestDataComponent> = ({ isConsult }) => {
  const { dispatch, state } = useDjurService()
  const { showModal } = useModal()
  const { setLoading } = useLoading()
  const form: UseFormReturn<any, object> & { errors?: any } = useFormContext()
  const [autocompleteContractOptions, setAutocompleteContractOptions] =
    useState<ISearchContractEntityResponse[]>([])
  const [isLoadingContract, setIsLoadingContract] = useState<boolean>(false)

  const listOptionsTypes = [
    {
      label: 'Centros de custo',
      value: 'Centros de custo'
    },
    {
      label: 'Subsidio de defesa',
      value: 'Subsidio de defesa'
    },
    {
      label: 'Informações para bloqueio',
      value: 'Informações para bloqueio'
    },
    {
      label: 'Outros',
      value: 'Outros'
    }
  ]
 

  const searchContract = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoadingContract(true)

    ContractServices()
      .searchContract({
        cdContrato: event.target.value
      })
      .then((response) => {
        if (response.status !== 200 || !response.data.sucesso) {
          throw new Error(
            response.data.mensagem || 'Houve um erro ao pesquisar os contratos'
          )
        }
      
        setAutocompleteContractOptions(response.data.entity)
        setIsLoadingContract(false)
      })
      .catch((error) => {
        setIsLoadingContract(false)
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const handleSearchCpfReclamante = () => {
    setLoading('loading')

    DjurServices()
      .searchReclamanteByCpfPassaporte({
        CpfPassaporte: FORMAT.unMask(form.getValues('cpfReclamante'))
      })
      .then((response) => {
        const success = 200
        const nocontent = 204

        if (![success, nocontent].includes(response.status)) {
          throw new Error('Houve um erro ao buscar os dados do reclamante')
        }

        form.setValue('nmReclamante', response.data?.nomeEfetivo)
        setLoading('idle')
      })
      .catch((error) => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })

    dispatch({
      payload: {
        tablesForRefresh: [
          'documentosParaDjur',
          'documentos',
          'dadosBloqueio',
          'anexarEvidencias',
          'historicoReclamante'
        ]
      },
      type: 'handleRefreshTable'
    })
  }

  useEffect(() => {
    dispatch({
      payload: {
        field: 'cpfReclamante',
        value: form.getValues('cpfReclamante')
      },
      type: 'handleChangeFormValue'
    })
  }, [form.watch('cpfReclamante')])

  useEffect(() => {
    dispatch({
      payload: {
        field: 'dtInicioReclamacao',
        value: form.getValues('dtInicioReclamacao')
      },
      type: 'handleChangeFormValue'
    })
  }, [form.watch('dtInicioReclamacao')])

  useEffect(() => {
    dispatch({
      payload: {
        field: 'dtFimReclamacao',
        value: form.getValues('dtFimReclamacao')
      },
      type: 'handleChangeFormValue'
    })
  }, [form.watch('dtFimReclamacao')])

  const cpfPassaporteReclamante = form.watch('cpfReclamante')

  const searchHandler = ((event: React.ChangeEvent<HTMLInputElement>) => {
    if(event.target.value.length > 0) {
      searchContract(event)
    }
  })

  return (
    <>
      <Grid
        gridCols="2fr 2fr 1.5fr 1fr 1fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`${SPACING.spacing10} 0 ${SPACING.spacing20} 0`}
      >
        <AutoCompleteSearchTerm
          control={form.control}
          errors={form.errors}
          formValue="cdContrato"
          disabled={state.isReanalize || state.isConsult}
          label="Contrato"
          placeholder="Nº"
          options={
            autocompleteContractOptions || [form.getValues('cdContrato')]
          }
          onChangeText={useDebounce(searchHandler, 600)}
          optionLabel="cdContrato"
          loading={isLoadingContract}
        />
      </Grid>
      <Grid
        gridCols="3fr 2fr 1.5fr 1fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
      >
        <Row>
          <DateRange
            watch={form.watch}
            control={form.control}
            errors={form.errors}
            label={['Inicio reclamação', 'Fim reclamação']}
            formValue={['dtInicioReclamacao', 'dtFimReclamacao']}
            width={['150px', '150px']}
            type={'monthAndYear'}
            disabled={state.isReanalize || state.isConsult}
          />
        </Row>
        <div />
        <InputMask
          control={form.control}
          errors={form.errors}
          formValue={'chamadoCervelo'}
          label="Chamado Cervelo"
          marginRight={SPACING.spacing15}
          placeholder="Nº"
          maxLength={20}
          type="alfanumeric"
          disabled={state.isReanalize || state.isConsult}
        />
        <InputMask
          control={form.control}
          errors={form.errors}
          formValue="nuAnalises"
          label="N° de análises"
          placeholder="Nº"
          maxLength={10}
          type="number"
          disabled={true}
        />
      </Grid>
      <Row marginTop={SPACING.spacing15}>
        <Input
          control={form.control}
          errors={form.errors}
          formValue="cpfReclamante"
          mask={
            isNaN(parseInt(cpfPassaporteReclamante))
              ? ['NN###########']
              : ['###.###.###-##']
          }
          label="CPF/Passaporte do Reclamante"
          maxLength={isNaN(parseInt(cpfPassaporteReclamante)) ? 14 : 14}
          placeholder="Nº"
          inputIcon={!state.isConsult && <MagnifyingGlassIcon />}
          onClickIcon={handleSearchCpfReclamante}
          disabled={state.isReanalize || state.isConsult}
        />
      </Row>
      <Row marginTop={SPACING.spacing15}>
        <InputMask
          control={form.control}
          errors={form.errors}
          formValue="nmReclamante"
          label="Nome"
          required
          placeholder="Nome do Reclamante"
          marginRight={SPACING.spacing15}
          maxLength={50}
          type="alfanumeric"
          disabled
        />
      </Row>
      <Row marginTop={SPACING.spacing15}>
        <Input
          control={form.control}
          errors={form.errors}
          formValue="nuProcesso"
          label="Processo"
          //masks="process"
          marginRight={SPACING.spacing15}
          placeholder="Nº"
          disabled={state.isConsult || state.isReanalize}
        />
        <CurrencyInput
          control={form.control}
          errors={form.errors}
          label="Valor da Causa"
          formValue="valorContratual"
          placeholder="R$"
          disabled={isConsult}
          marginRight={SPACING.spacing15}
          required
        />
        <SelectForm
          control={form.control}
          errors={form.errors}
          formValue="tipo"
          label="Tipo"
          listOptions={listOptionsTypes}
          labelMenu="label"
          valueMenu="value"
          defaultValue={""}
          placeholder="Placeholder"
          width="300px"
          
          disabled={isConsult}
          required
        />
      </Row>
      <Row marginTop={SPACING.spacing15}>
        <DateRange
          watch={form.watch}
          control={form.control}
          errors={null}
          label={['Data de Entrada', 'Data de Retorno']}
          formValue={['dtEntrada', 'dtRetorno']}
          width={['150px', '150px']}
          marginRight={[null, SPACING.spacing15]}
          disabled
        />
        <InputMask
          control={form.control}
          errors={form.errors}
          formValue="txObservacaoRetorno"
          label="Observação de Retorno"
          placeholder="Observação"
          width="605px"
          maxLength={70}
          disabled={isConsult}
        />
      </Row>
    </>
  )
}

export default RequestData
